import { Component } from '@theme/component';

/**
 * A custom element that shows a countdown "reserving" the cart contents.
 * The displayed countdown loops back to the full segment duration every
 * segment duration seconds (e.g. resets to 10:00 every 10 minutes) forever -
 * purely a visual urgency cue.
 *
 * This used to also clear the cart via the Cart API once a separate, real
 * (non-looping) total duration elapsed underneath the looping display -
 * removed per explicit request, so a customer's cart is never emptied
 * automatically no matter how long the countdown has been looping. Only the
 * segment-loop and its own imports/logic remain; the total-duration/expiry
 * tracking, sessionStorage persistence, and #clearCart() are gone with it,
 * since nothing here needs them anymore.
 *
 * @typedef {Object} CartReservationTimerRefs
 * @property {HTMLElement} timerValue - The element displaying the remaining mm:ss.
 *
 * @extends {Component<CartReservationTimerRefs>}
 */
class CartReservationTimer extends Component {
  requiredRefs = ['timerValue'];

  /** @type {number | undefined} */
  #intervalId;

  /** @type {number} */
  #startedAt = 0;

  /** @type {number} */
  #segmentDuration = 0;

  /** @type {MutationObserver | undefined} */
  #healObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#segmentDuration = Number(this.dataset.segmentDuration) || 600;
    this.#startedAt = Date.now();

    this.#tick();
    this.#intervalId = window.setInterval(this.#tick, 1000);

    // The Section Rendering API morphs this element on every cart update
    // (add-to-cart, quantity change) since it lives inside the re-rendered
    // drawer section. This element itself survives the morph (same live
    // instance, #startedAt/#intervalId untouched), but the morph overwrites
    // its `class` attribute and the timer span's text back to their plain
    // server-rendered values (no --ready class, empty span), since the
    // server has no way to know the client-side countdown value.
    //
    // The obvious fix - restoring state in the Component base class's
    // `updatedCallback` hook - doesn't work: morph's own `onAfterUpdate`
    // checks `instanceof Component` against the *freshly-parsed response
    // node*, not this live element, and DOMParser-parsed nodes are never
    // upgraded custom elements per spec, so that check is always false and
    // updatedCallback never fires. Watching the live DOM directly instead
    // catches every such reset regardless of how morph triggers it.
    // #tick() only writes when the value actually differs, so this settles
    // after one harmless extra pass instead of looping on its own writes.
    this.#healObserver = new MutationObserver(this.#tick);
    this.#healObserver.observe(this, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true,
      childList: true,
      characterData: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearInterval(this.#intervalId);
    this.#healObserver?.disconnect();
  }

  #tick = () => {
    const elapsed = Math.max(0, Math.round((Date.now() - this.#startedAt) / 1000));
    const elapsedInSegment = elapsed % this.#segmentDuration;
    const segmentRemaining = this.#segmentDuration - elapsedInSegment;

    const minutes = Math.floor(segmentRemaining / 60);
    const seconds = segmentRemaining % 60;
    const text = `${minutes}:${String(seconds).padStart(2, '0')}`;

    if (this.refs.timerValue.textContent !== text) {
      this.refs.timerValue.textContent = text;
    }
    if (!this.classList.contains('cart-reservation-timer--ready')) {
      this.classList.add('cart-reservation-timer--ready');
    }
  };
}

if (!customElements.get('cart-reservation-timer')) {
  customElements.define('cart-reservation-timer', CartReservationTimer);
}